import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Package, Truck, Check, RefreshCw, XCircle, Search, Lock, CalendarDays, Banknote, MapPin, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { API, fmt } from "../constants/site";
import Logo from "../components/Logo";

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" });

export default function TrackOrderPage() {
  const [params, setParams] = useSearchParams();
  const orderNumber = (params.get("order") || "").trim().toUpperCase();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchVal, setSearchVal] = useState(orderNumber);
  const [cancelling, setCancelling] = useState(false);
  const [returnOpen, setReturnOpen] = useState(params.get("return") === "1");
  const [returnReason, setReturnReason] = useState("");
  const [requesting, setRequesting] = useState(false);

  const load = (num, silent = false) => {
    const target = (num || "").trim().toUpperCase();
    if (!target) {
      setOrder(null);
      setError("");
      return;
    }
    if (!silent) setLoading(true);
    fetch(`${API}/orders/${encodeURIComponent(target)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("not found"))))
      .then((d) => {
        setOrder(d);
        setError("");
      })
      .catch(() => {
        if (!silent) {
          setOrder(null);
          setError("We couldn't find that order number. Double-check it and try again.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (orderNumber) load(orderNumber);
    else {
      setOrder(null);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  // Auto-refresh every 30s — when the owner ships from the dashboard, buyers see it here without doing anything
  useEffect(() => {
    if (!orderNumber) return;
    const id = setInterval(() => load(orderNumber, true), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const search = (e) => {
    e.preventDefault();
    const v = searchVal.trim().toUpperCase();
    if (!v) return;
    setParams({ order: v });
  };

  const cancelOrder = async () => {
    if (!order || !window.confirm("Are you sure you want to cancel this order?")) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API}/orders/${order.order_number}/customer-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "cancelled" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not cancel this order");
      setOrder(data);
      toast.success("Your order has been cancelled");
    } catch (e2) {
      toast.error(e2.message || "Could not cancel this order");
    } finally {
      setCancelling(false);
    }
  };

  const returnDaysLeft = (() => {
    if (!order?.delivered_at) return null;
    const d = new Date(order.delivered_at);
    if (isNaN(d.getTime())) return null;
    return Math.max(0, Math.ceil((6 * 86400e3 - (Date.now() - d.getTime())) / 86400e3));
  })();
  const returnWindowClosed = returnDaysLeft !== null && returnDaysLeft <= 0;
  const windowEndDate = order?.delivered_at
    ? new Date(new Date(order.delivered_at).getTime() + 6 * 86400e3).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : "";

  const requestReturn = async () => {
    if (!order) return;
    setRequesting(true);
    try {
      const res = await fetch(`${API}/orders/${order.order_number}/return-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: returnReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not request the return");
      setOrder(data);
      setReturnOpen(false);
      toast.success("Return requested — we'll contact you shortly");
    } catch (e2) {
      toast.error(e2.message || "Could not request the return");
    } finally {
      setRequesting(false);
    }
  };

  const st = order?.status;
  const cancelled = st === "cancelled";
  const shipped = ["shipped", "delivered", "returned"].includes(st);
  const delivered = st === "delivered";
  const returnRequested = st === "return_requested";
  const canCancel = st === "confirmed";
  const awb = order?.shipment?.awb;

  const stepDot = (done, Icon) => (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
        done ? "bg-sage-500 text-white" : "border-2 border-charcoal-900/15 bg-white text-charcoal-500/50"
      }`}
    >
      <Icon className="h-4 w-4" />
    </span>
  );

  return (
    <div className="min-h-screen bg-oat-100" data-testid="track-order-page">
      <header className="border-b border-charcoal-900/10 bg-oat-50/90 backdrop-blur">
        <div className="mx-auto flex h-[72px] max-w-3xl items-center justify-between px-5">
          <Link to="/" aria-label="Back to store">
            <Logo />
          </Link>
          <Link to="/" className="text-sm text-charcoal-500 transition-colors hover:text-sage-600">
            Continue shopping
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12">
        <div className="rounded-3xl border border-charcoal-900/10 bg-white p-8 md:p-12">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sage-600">Order Tracking</p>
          <h1 className="mt-2 font-serif text-4xl text-charcoal-900">Where's my OmniMist?</h1>
          <p className="mt-2 text-sm text-charcoal-500">
            Enter your order number (like OM-A1B2C3D4) — you'll find it in your confirmation email or our WhatsApp message.
          </p>

          <form onSubmit={search} className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="track-search-form">
            <input
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value.toUpperCase())}
              placeholder="Enter order number, e.g. OM-A1B2C3D4"
              data-testid="track-input"
              className="w-full flex-1 rounded-full border border-charcoal-900/15 bg-oat-50 px-5 py-3.5 text-base text-charcoal-900 outline-none transition-colors focus:border-sage-500"
            />
            <button
              type="submit"
              data-testid="track-submit-btn"
              className="flex items-center justify-center gap-2 rounded-full bg-charcoal-900 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600"
            >
              <Search className="h-4 w-4" /> Track Order
            </button>
          </form>

          {loading && (
            <p className="mt-8 flex items-center gap-2 text-charcoal-500" data-testid="track-loading">
              <RefreshCw className="h-4 w-4 animate-spin" /> Loading your order…
            </p>
          )}

          {!loading && error && (
            <p className="mt-8 rounded-2xl border border-gold/40 bg-gold/10 p-5 text-sm text-[#8a6a2f]" data-testid="track-error">
              {error}
            </p>
          )}

          {!loading && order && (
            <div className="mt-8" data-testid="track-result">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-oat-100 p-5">
                <div>
                  <p className="font-serif text-2xl text-charcoal-900" data-testid="track-order-number">
                    {order.order_number}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-charcoal-500" data-testid="track-order-date">
                    <CalendarDays className="h-3.5 w-3.5 text-sage-600" /> {fmtDate(order.created_at)}
                  </p>
                </div>
                <span
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${
                    cancelled
                      ? "bg-[#fdecec] text-[#b91c1c]"
                      : returnRequested
                      ? "bg-amber-100 text-amber-700"
                      : delivered
                      ? "bg-sage-700 text-white"
                      : shipped
                      ? "bg-sage-500 text-white"
                      : "bg-oat-200 text-charcoal-700"
                  }`}
                  data-testid="track-status-chip"
                >
                  {cancelled || returnRequested ? (
                    <XCircle className="h-3.5 w-3.5" />
                  ) : shipped ? (
                    <Truck className="h-3.5 w-3.5" />
                  ) : (
                    <Package className="h-3.5 w-3.5" />
                  )}
                  {cancelled ? "Cancelled" : returnRequested ? "Return requested" : delivered ? "Delivered" : shipped ? "Shipped" : "Confirmed"}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-charcoal-900/10 p-4" data-testid="track-order-items">
                  <p className="text-xs uppercase tracking-[0.14em] text-charcoal-500">Items</p>
                  {order.items.map((it, i) => (
                    <p key={i} className="mt-1 text-sm text-charcoal-700">
                      {it.quantity}× OmniMist™ 2-in-1 Sprayer & Dispenser — {fmt(it.line_total)}
                    </p>
                  ))}
                  <p className="mt-2 flex items-center gap-2 border-t border-charcoal-900/10 pt-2 text-sm">
                    <Banknote className="h-3.5 w-3.5 text-sage-600" />
                    <span className="text-charcoal-500">
                      {order.payment_method === "cod" ? "Cash on Delivery" : "Paid online"} ·{" "}
                      <span className="font-semibold text-charcoal-900" data-testid="track-order-total">{fmt(order.total)}</span>
                    </span>
                  </p>
                </div>
                <div className="rounded-2xl border border-charcoal-900/10 p-4" data-testid="track-order-address">
                  <p className="text-xs uppercase tracking-[0.14em] text-charcoal-500">Delivering to</p>
                  <p className="mt-1 flex items-start gap-1.5 text-sm text-charcoal-700">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sage-600" />
                    <span>
                      {order.customer.full_name}, {order.customer.address}, {order.customer.city}, {order.customer.state} — {order.customer.zip}
                    </span>
                  </p>
                </div>
              </div>

              {cancelled ? (
                <div className="mt-6 rounded-2xl bg-[#fdecec] p-6 text-center" data-testid="track-cancelled-panel">
                  <XCircle className="mx-auto h-10 w-10 text-[#b91c1c]" />
                  <p className="mt-3 font-serif text-2xl text-charcoal-900">This order is cancelled</p>
                  <p className="mt-1 text-sm text-charcoal-500">
                    Since it was Cash on Delivery, nothing was charged. Changed your mind? Order again anytime!
                  </p>
                </div>
              ) : (
                <>
                  <ol className="mt-8 space-y-6">
                    <li className="flex items-start gap-4">
                      {stepDot(true, Check)}
                      <div>
                        <p className="font-medium text-charcoal-900">Order placed</p>
                        <p className="text-sm text-charcoal-500">Thank you for your order!</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      {stepDot(shipped, Truck)}
                      <div>
                        <p className={`font-medium ${shipped ? "text-charcoal-900" : "text-charcoal-500"}`}>Shipped</p>
                        <p className="text-sm text-charcoal-500">
                          {awb
                            ? `On its way with ${order.shipment.courier || "our courier"} · AWB ${awb}`
                            : shipped
                            ? "Handed to the courier — tracking number coming soon"
                            : "Packing begins within 24 hours of your order"}
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      {stepDot(delivered, Check)}
                      <div>
                        <p className={`font-medium ${delivered ? "text-charcoal-900" : "text-charcoal-500"}`}>Delivered</p>
                        <p className="text-sm text-charcoal-500">
                          {delivered
                            ? "Enjoy your OmniMist! We'd love to hear how it works for you."
                            : "Expected in 2–4 business days. The courier calls before delivery."}
                        </p>
                      </div>
                    </li>
                  </ol>

                  {awb && (
                    <div className="mt-6 rounded-2xl border border-sage-500/30 bg-sage-100/60 p-5" data-testid="track-awb-box">
                      <p className="text-sm text-charcoal-700">
                        AWB <span className="font-mono font-semibold text-charcoal-900">{awb}</span> ·{" "}
                        {order.shipment.courier || "Bluedart"}
                      </p>
                      <a
                        href={order.shipment.courier === "Delhivery" ? `https://www.delhivery.com/track?awb=${encodeURIComponent(awb)}` : `https://www.bluedart.com/trackdart.aspx?trackNo=${encodeURIComponent(awb)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="track-bluedart-btn"
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-charcoal-900 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600"
                      >
                        <Truck className="h-3.5 w-3.5" /> Track live on {order.shipment.courier || "Bluedart"}
                      </a>
                    </div>
                  )}

                  {returnRequested ? (
                    <div className="mt-6 rounded-2xl bg-amber-50 p-5 text-sm text-amber-800" data-testid="track-return-requested-panel">
                      <p className="flex items-start gap-2">
                        <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
                        Your return request is in — our team will contact you on WhatsApp to arrange the pickup and refund.
                      </p>
                      {(order.return_pickup || {}).awb && (
                        <p className="mt-3 rounded-xl bg-white px-3 py-2 text-xs text-amber-800" data-testid="track-return-awb">
                          Return pickup booked — Delhivery AWB <b>{order.return_pickup.awb}</b>. Keep the sprayer's box ready for the courier.
                        </p>
                      )}
                    </div>
                  ) : delivered ? (
                    <div className="mt-6 rounded-2xl border border-charcoal-900/10 bg-oat-50 p-5" data-testid="track-return-box">
                      {!returnWindowClosed ? (
                        <>
                          <p className="text-sm text-charcoal-500">
                            Not happy with your OmniMist? Start a return here — we'll arrange the pickup and refund.
                          </p>
                          <p className="mt-2 text-xs font-medium uppercase tracking-[0.1em] text-amber-700" data-testid="track-return-window">
                            Return window: {returnDaysLeft} day{returnDaysLeft === 1 ? "" : "s"} left (until {windowEndDate})
                          </p>
                          {!returnOpen ? (
                            <button
                              onClick={() => setReturnOpen(true)}
                              data-testid="track-return-open-btn"
                              className="mt-3 flex items-center gap-2 rounded-full border-2 border-charcoal-900/25 bg-white px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-700 transition-colors hover:border-sage-500 hover:text-sage-700"
                            >
                              <RotateCcw className="h-3.5 w-3.5" /> Request a return
                            </button>
                          ) : (
                        <>
                          <label className="text-xs uppercase tracking-[0.14em] text-charcoal-500" htmlFor="return-reason">
                            Why are you returning it?
                          </label>
                          <textarea
                            id="return-reason"
                            value={returnReason}
                            onChange={(e) => setReturnReason(e.target.value)}
                            rows={3}
                            placeholder="e.g. Sprayer stopped misting after a few uses"
                            data-testid="track-return-reason-input"
                            className="mt-2 w-full rounded-2xl border border-charcoal-900/15 bg-white px-4 py-3 text-base text-charcoal-900 outline-none transition-colors focus:border-sage-500"
                          />
                          <div className="mt-3 flex gap-2">
                            <button
                              onClick={requestReturn}
                              disabled={requesting || returnReason.trim().length < 10}
                              data-testid="track-return-submit-btn"
                              className="rounded-full bg-charcoal-900 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600 disabled:opacity-50"
                            >
                              {requesting ? "Submitting…" : "Submit return request"}
                            </button>
                            <button
                              onClick={() => setReturnOpen(false)}
                              data-testid="track-return-cancel-btn"
                              className="rounded-full border-2 border-charcoal-900/25 bg-white px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-700 transition-colors hover:border-sage-500 hover:text-sage-700"
                            >
                              Keep it
                            </button>
                          </div>
                        </>
                      )}
                        </>
                      ) : (
                        <p className="text-sm text-charcoal-500" data-testid="track-return-window-closed">
                          The return window has closed — returns can be requested within 6 days of delivery. Write to us on WhatsApp and we'll still help.
                        </p>
                      )}
                    </div>
                  ) : null}

                  {canCancel ? (
                    <div className="mt-6 rounded-2xl border border-charcoal-900/10 bg-oat-50 p-5" data-testid="track-cancel-box">
                      <p className="text-sm text-charcoal-500">
                        Changed your mind? Cancel free of charge any time before your order ships.
                      </p>
                      <button
                        onClick={cancelOrder}
                        disabled={cancelling}
                        data-testid="track-cancel-btn"
                        className="mt-3 flex items-center gap-2 rounded-full border-2 border-[#b91c1c]/40 bg-white px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#b91c1c] transition-colors hover:bg-[#fdecec] disabled:opacity-60"
                      >
                        <XCircle className="h-3.5 w-3.5" /> {cancelling ? "Cancelling…" : "Cancel this order"}
                      </button>
                    </div>
                  ) : shipped ? (
                    <p
                      className="mt-6 flex items-start gap-2 rounded-2xl bg-sage-100/70 p-5 text-sm text-sage-700"
                      data-testid="track-cancel-blocked-note"
                    >
                      <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                      Your order is on its way, so cancellation has closed. Message us on WhatsApp — we'll happily help with returns.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          )}

          {!loading && order && (
            <button
              onClick={() => load(orderNumber)}
              data-testid="track-refresh-btn"
              className="mt-8 flex items-center gap-2 rounded-full border-2 border-charcoal-900 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-900 transition-all hover:bg-sage-100 active:scale-95"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh status
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
